import { computed, makeObservable } from 'mobx'
import type { Moment } from 'moment'
import moment from 'moment'

import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import SurveysFormPolicy from 'modules/core/store/models/policies/SurveysFormPolicy'

import { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'
import questionGroupConfigs from 'modules/reviews/store/collections/questionGroupConfigs'
import questionGroups from 'modules/reviews/store/collections/questionGroups'
import type QuestionGroup from 'modules/reviews/store/models/QuestionGroup'
import type QuestionGroupConfig from 'modules/reviews/store/models/QuestionGroupConfig'

export const Status = {
  draft: 'draft',
  scheduled: 'scheduled',
  active: 'active',
  finished: 'finished',
}

enum Roles {
  author = 'author',
  manager = 'manager',
  supervisor = 'supervisor',
  team_lead = 'team_lead',
}

interface UserRoles {
  [Roles.author]: boolean
  [Roles.manager]: boolean
  [Roles.supervisor]: boolean
  [Roles.team_lead]: boolean
}

interface CycleInfo {
  id: number
  closed_at: string | null
  ends_at: string | null
  number_of_answers: number
  starts_at: string | null
}

export interface IReviewSetting {
  include_competencies_assessments?: boolean
  show_score_toggle?: boolean
  include_review_scoring?: boolean
}

export interface IReview {
  id: number
  access_id: number
  name: string
  start_on: string
  end_on: string
  frequency: string
  status: string
  description: string
  visible_to_managers: boolean
  hide_answers_for_target: boolean
  anonymous: boolean
  author_id: number
  supervisor_ids: number[]
  duration_in_days: string
  scheduled_time: string
  question_groups_count: number
  answered_questions_count: number
  answered_question_groups_count: number
  role: UserRoles
  current_cycle: CycleInfo
  setting: IReviewSetting | null
}

class Review extends ModelWithPolicy<IReview> implements PolicyTarget {
  static Status = Status

  constructor(attr: IReview) {
    super(attr)
    makeObservable(this, {
      isFinished: computed,
      isDraft: computed,
      isScheduled: computed,
      isActive: computed,
      isAnonymous: computed,
      answeredQuestionsCount: computed,
      questionGroupConfigs: computed,
      isVisibleToManagers: computed,
      isHidingAnswersToReviewees: computed,
      assessCompetencies: computed,
      hasScoreQuestions: computed,
      showScoreToggle: computed,
    })
  }

  get policy() {
    return new SurveysFormPolicy(this)
  }

  get policyTargetClass(): string {
    return '::PerformanceReviews::Review'
  }

  get indexes() {
    return ['access_id', 'surveys_form_id']
  }

  static urlRoot() {
    return '/performance_reviews/reviews'
  }

  startReview() {
    return this.rpc('start')
  }

  stopReview() {
    return this.rpc('stop')
  }

  remindParticipants() {
    return this.rpc('notify')
  }

  get name(): string {
    return this.attributes.name
  }

  get roles(): UserRoles {
    return this.attributes.role
  }

  get isDraft(): boolean {
    return this.attributes.status === Status.draft
  }

  get isScheduled(): boolean {
    return this.attributes.status === Status.scheduled
  }

  get isActive(): boolean {
    return this.attributes.status === Status.active
  }

  get isFinished(): boolean {
    return this.attributes.status === Status.finished
  }

  get isAnonymous(): boolean {
    return this.attributes.anonymous
  }

  get isVisibleToManagers(): boolean {
    return this.attributes.visible_to_managers
  }

  get isHidingAnswersToReviewees(): boolean {
    return this.attributes.hide_answers_for_target
  }

  get isStartOnSet(): boolean {
    return this.attributes.start_on != null
  }

  get isEndOnSet(): boolean {
    return this.attributes.end_on != null
  }

  get isScheduleTimeSet(): boolean {
    return this.attributes.scheduled_time != null
  }

  get startOnUtcDateTime(): Moment | undefined {
    if (this.isStartOnSet && this.isScheduleTimeSet) {
      return moment.utc(this.attributes.start_on + ' ' + this.attributes.scheduled_time)
    } else {
      return undefined
    }
  }

  get currentCycleStartOnUtcDateTime(): Moment | undefined {
    if (this.isStartOnSet && this.attributes.current_cycle) {
      return moment.utc(this.attributes.current_cycle.starts_at)
    } else {
      return undefined
    }
  }

  get isDaysToAnswerSet(): boolean {
    return this.attributes.duration_in_days != null
  }

  get answeredQuestionsCount(): number {
    return this.attributes.answered_questions_count
  }

  get answeredQuestionGroupsCount(): number {
    return this.attributes.answered_question_groups_count
  }

  get hasSchedule(): boolean {
    return this.attributes.frequency !== 'single'
  }

  get hasAnswers(): boolean {
    return this.attributes.answered_questions_count > 0
  }

  get questionGroups(): QuestionGroup[] {
    return questionGroups.filter({
      surveys_form_id: this.attributes.id,
    })
  }

  get questionGroupConfigs(): QuestionGroupConfig[] {
    return questionGroupConfigs.filter({
      surveys_form_id: this.attributes.id,
    })
  }

  fetchRespondents(): Promise<unknown> {
    return this.rpc('respondents')
  }

  get currentCycleId(): number {
    return this.attributes.current_cycle.id
  }

  get cycleHasAnswers(): boolean {
    return this.attributes.current_cycle.number_of_answers > 0
  }

  get assessCompetencies(): boolean {
    return this.attributes.setting?.include_competencies_assessments || false
  }

  get hasScoreQuestions(): boolean {
    return this.attributes.setting?.include_review_scoring || false
  }

  get showScoreToggle(): boolean {
    return this.attributes.setting?.show_score_toggle || false
  }
}

export default Review
